import React, { useMemo, type FC } from 'react';
import {
  Composites,
  TextLabel,
  TextInputMultiline,
  Button,
} from '@wix/wix-base-ui';
import {
  AiInfoBlock,
  AiTermsAndConditions,
  BusinessInfo,
} from '#packages/baseUI';
import { connectWithScope } from '#packages/apilib';
import { translate } from '#packages/i18n';
import styles from './promptCreator.scss';
import { AddPresetScope } from '../../../../addPresetApiEntry';
import {
  mapStateToProps,
  mapDispatchToProps,
  type AiHtmlSectionCreatorStateProps,
  type AiHtmlSectionCreatorDispatchProps,
} from './aiHtmlPromptCreatorMapper';
import { SparklesBoldSmall } from '@wix/wix-ui-icons-common/classic-editor';
import {
  AI_SECTION_CREATOR_PANEL_TYPE,
  BUSINESS_TYPE_PANEL_ORIGIN,
  MAX_DESCRIPTION_LENGTH,
} from '../aiSectionCreatorConsts';
import { smartForm } from '#packages/util';
import { getDescirptionErrorMessage } from '../utils';
import Loader from './loader';
import {
  HtmlPreviewThumbnail,
  HtmlPreviewThumbnailManager,
} from '#packages/addPreset';
import experiment from 'experiment';

export interface AiHtmlPromptCreatorOwnProps {
  cachedData: { categoryId: string; description: string };
  sessionId: string;
}

type AiHtmlPromptCreatorProps = AiHtmlPromptCreatorOwnProps &
  AiHtmlSectionCreatorStateProps &
  AiHtmlSectionCreatorDispatchProps;

const emptyArray: [] = [];

const AiHtmlPromptCreator: FC<AiHtmlPromptCreatorProps> = ({
  isLoading,
  generatedSection,
  cachedData,
  sessionId,
  handleFormSubmit,
}) => {
  const descirptionFieldControls = smartForm.useSmartField<string>(
    cachedData.description,
    [smartForm.maxLengthValidator(MAX_DESCRIPTION_LENGTH)],
  );

  const isFormInvalid =
    !descirptionFieldControls.isValid && descirptionFieldControls.isDirty;

  const handleSubmitButtonClick = () => {
    const isFormValid = descirptionFieldControls.isValid;
    if (!isFormValid) return;

    handleFormSubmit(descirptionFieldControls.value);
  };

  const handleDescriptionChange = (value: string) =>
    descirptionFieldControls.setValue(value);

  const handleDescriptionBlur = () => {
    descirptionFieldControls.setDirty();
  };

  const htmlPreviewThumbnailManager = useMemo<
    HtmlPreviewThumbnailManager | undefined
  >(
    () => generatedSection && new HtmlPreviewThumbnailManager(generatedSection),
    [generatedSection],
  );

  if (isLoading) {
    return <Loader />;
  }

  const generateSubtitleText: string = experiment.isOpen('se_newContentForAI')
    ? translate('add_section_ai_generate_panel_subtitle')
    : translate('add_section_ai_panel_generate_subtitle');

  return (
    <div
      className={styles.wrapper}
      data-hook="ai-section-creator_prompt-creator"
    >
      <AiInfoBlock
        title={translate('add_section_ai_panel_title')}
        text={generateSubtitleText}
      />
      <div className={styles.formWrapper}>
        <BusinessInfo
          origin={BUSINESS_TYPE_PANEL_ORIGIN}
          infoIconText="add_section_ai_site_info_tooltip_text"
        />
        <Composites.TextInputLabeled>
          <TextLabel
            type="T02"
            shouldTranslate={false}
            // todo: add translation key
            value={'The section should include'}
          />
          <TextInputMultiline
            value={descirptionFieldControls.value}
            shouldTranslate={false}
            className={styles.multilineInput}
            onChange={handleDescriptionChange}
            onBlur={handleDescriptionBlur}
            dataHook="ai-section-creator_description"
            isValid={
              !descirptionFieldControls.isDirty ||
              descirptionFieldControls.isValid
            }
            invalidMessage={getDescirptionErrorMessage(
              descirptionFieldControls.errors,
            )}
            // todo: add translation key
            placeholder="2 columns layout"
          />
        </Composites.TextInputLabeled>
      </div>
      <div className={styles.submitWrapper}>
        <Button
          dataHook="ai-section-creator_submit"
          onClick={handleSubmitButtonClick}
          className="btn-md btn-ai-primary"
          disabled={isFormInvalid}
          suffixIcon={<SparklesBoldSmall />}
        >
          {experiment.isOpen('se_newContentForAI')
            ? translate('add_section_ai_generate_section_cta')
            : translate('add_section_ai_create_section_generate_cta')}
        </Button>
      </div>
      {!!htmlPreviewThumbnailManager && (
        <HtmlPreviewThumbnail
          previewHtmlManager={htmlPreviewThumbnailManager}
          shouldOptimizeImages={true}
          containerWidth={300}
          containerHeight={150}
          fontsUrls={emptyArray}
        />
      )}
      <div className={styles.termsWrapper}>
        <AiTermsAndConditions
          translationKey={'add_section_ai_panel_generate_legal'}
          panelType={AI_SECTION_CREATOR_PANEL_TYPE}
          sessionId={sessionId}
        />
      </div>
    </div>
  );
};

export default connectWithScope(
  () => AddPresetScope,
  AiHtmlPromptCreator,
  mapStateToProps,
  mapDispatchToProps,
);
